import React, { useEffect, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Componente Modal reutilizable con animaciones Framer Motion
 *
 * Uso:
 * <Modal isOpen={true} onClose={handleClose} size="md">
 *   <Modal.Header onClose={handleClose}>Título</Modal.Header>
 *   <Modal.Body>Contenido</Modal.Body>
 *   <Modal.Footer>
 *     <button onClick={handleClose}>Cancelar</button>
 *   </Modal.Footer>
 * </Modal>
 *
 * Props:
 * - isOpen: boolean - Controla si el modal está visible
 * - onClose: function - Función llamada al cerrar el modal
 * - size: 'sm' | 'md' | 'lg' | 'xl' | 'full' - Tamaño del modal (default: 'md')
 * - closeOnOverlay: boolean - Si se cierra al hacer clic en el overlay (default: false)
 * - closeOnEscape: boolean - Si se cierra al presionar Escape (default: true)
 * - className: string - Clases adicionales para el contenido del modal
 *
 * Modal.Header Props:
 * - onClose: function - Muestra botón X arriba a la derecha
 * - showCloseButton: boolean - Mostrar/ocultar botón X (default: true)
 */

// Animation variants
const overlayVariants = {
  hidden: {
    opacity: 0,
    backdropFilter: "blur(0px)"
  },
  visible: {
    opacity: 1,
    backdropFilter: "blur(4px)",
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    backdropFilter: "blur(0px)",
    transition: {
      duration: 0.15
    }
  }
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 40
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 350,
      damping: 25
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 30,
    transition: {
      duration: 0.15,
      ease: "easeIn"
    }
  }
};

const Modal = ({
  isOpen,
  onClose,
  size = 'md',
  closeOnOverlay = false,
  closeOnEscape = true,
  className = '',
  children
}) => {
  // Cerrar con Escape
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEscape) {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevenir scroll del body cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlay) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    '3xl': 'max-w-7xl',
    full: 'max-w-[95vw]'
  };

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="modal-overlay"
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className={`modal-content ${sizeClasses[size] || sizeClasses.md} ${className}`}
            onClick={(e) => e.stopPropagation()}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Usar portal para renderizar fuera del DOM principal
  return createPortal(modalContent, document.body);
};

// Subcomponente Header
const Header = ({ children, onClose, showCloseButton = true, className = '' }) => (
  <div className={`modal-header bg-white ${className}`}>
    <h2 className="text-lg font-semibold text-gray-800 m-0 flex-1">{children}</h2>
    {showCloseButton && onClose && (
      <motion.button
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ml-4 flex-shrink-0"
        onClick={onClose}
        aria-label="Cerrar modal"
        whileHover={{ scale: 1.1, rotate: 90 }}
        whileTap={{ scale: 0.9 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        <FaTimes className="w-5 h-5" />
      </motion.button>
    )}
  </div>
);

// Subcomponente Body
const Body = ({ children, className = '' }) => (
  <div className={`modal-body ${className}`}>
    {children}
  </div>
);

// Subcomponente Footer
const Footer = ({ children, className = '' }) => (
  <div className={`modal-footer ${className}`}>
    {children}
  </div>
);

// Asignar subcomponentes
Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
